import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, FileText, Calendar, Clock, User, Target, TrendingUp, Award, BarChart3 } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Badge from '../../components/Common/Badge';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const AssessmentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringData, setScoringData] = useState(null);
  const [scoringFilters, setScoringFilters] = useState({
    collegeId: '',
    department: '',
    batch: '',
    studentId: '',
    search: ''
  });

  useEffect(() => {
    fetchAssessment();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'scoring') {
      fetchScoringAnalytics();
    }
  }, [activeTab, id, scoringFilters.collegeId, scoringFilters.department, scoringFilters.batch, scoringFilters.studentId, scoringFilters.search]);

  const fetchAssessment = async () => {
    try {
      const { data } = await api.get(`/assessments/${id}`);
      if (data.success) {
        setAssessment(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch assessment');
    } finally {
      setLoading(false);
    }
  };

  const fetchScoringAnalytics = async () => {
    try {
      setScoringLoading(true);
      const params = new URLSearchParams();
      Object.entries(scoringFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const { data } = await api.get(`/assessments/${id}/responses?${params.toString()}`);
      if (data.success) {
        setScoringData(data.data);
        if (data.data.assessment) {
          setAssessment(prev => ({ ...prev, ...data.data.assessment }));
        }
      }
    } catch (error) {
      toast.error('Failed to fetch scoring analytics');
    } finally {
      setScoringLoading(false);
    }
  };

  const handleScoringFilterChange = (field, value) => {
    setScoringFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'collegeId' ? { department: '', batch: '', studentId: '' } : {}),
      ...(field === 'department' ? { studentId: '' } : {}),
      ...(field === 'batch' ? { studentId: '' } : {})
    }));
  };

  const downloadFile = () => {
    if (assessment?.file) {
      window.open(assessment.file, '_blank');
      toast.success('Downloading file');
    } else {
      toast.error('No file available');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Assessment not found</p>
        <Button onClick={() => navigate('/assessments')} className="mt-4">
          Back to Assessments
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'scoring', label: 'Scoring Analytics', icon: TrendingUp },
  ];

  // Real scoring aggregation from the analytics endpoint.
  const getScoringAnalytics = () => {
    return scoringData?.analytics?.totalResponses > 0 ? scoringData.analytics : null;
  };

  const scoringAnalytics = getScoringAnalytics();
  const scoringResponses = scoringData?.responses || scoringData?.assessment?.responses || [];
  const scoringFilterOptions = scoringData?.filters || {};
  const COLORS = ['#ef4444', '#f59e0b', '#0ea5e9', '#10b981'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={ArrowLeft}
            onClick={() => navigate('/assessments')}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff]">
              {assessment.assessmentName}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {assessment.assessmentCode} • {assessment.questionCategory}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {assessment.file && (
            <Button
              variant="outline"
              icon={Download}
              onClick={downloadFile}
            >
              Download File
            </Button>
          )}
          <Button
            variant="outline"
            icon={Edit}
            onClick={() => navigate(`/assessments/edit/${id}`)}
          >
            Edit
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card title="Basic Information">
            <div className="space-y-4">
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Assessment Name</p>
                  <p className="text-base text-gray-900">{assessment.assessmentName}</p>
                </div>
              </div>

              <div className="flex items-start">
                <FileText className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Assessment Code</p>
                  <p className="text-base font-mono font-semibold text-primary-600">
                    {assessment.assessmentCode || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Target className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Question Category</p>
                  <Badge variant="primary">{assessment.questionCategory}</Badge>
                </div>
              </div>

              <div className="flex items-start">
                <FileText className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <Badge variant={
                    assessment.status === 'active' ? 'success' :
                      assessment.status === 'inactive' ? 'danger' : 'warning'
                  }>
                    {assessment.status}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Schedule Information */}
          <Card title="Schedule Information">
            <div className="space-y-4">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Created Date</p>
                  <p className="text-base text-gray-900">
                    {assessment.createdAt ? format(new Date(assessment.createdAt), 'dd MMMM yyyy') : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Clock className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Duration</p>
                  <p className="text-base text-gray-900">
                    {assessment.duration ? `${assessment.duration} minutes` : 'Not specified'}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Target className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Questions</p>
                  <p className="text-base text-gray-900">
                    {assessment.totalQuestions !== undefined && assessment.totalQuestions !== null
                      ? assessment.totalQuestions
                      : (assessment.questions?.length || 0)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <TrendingUp className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Max Attempts Allowed</p>
                  <p className="text-base text-gray-900">
                    {assessment.maxAttempts !== undefined && assessment.maxAttempts !== null
                      ? assessment.maxAttempts
                      : 1}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Target className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Questions per Attempt</p>
                  <p className="text-base text-gray-900">
                    {assessment.questionsPerAttempt ? assessment.questionsPerAttempt : 'All'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Description */}
          <Card title="Description" className="lg:col-span-2">
            <p className="text-gray-700 whitespace-pre-wrap">{assessment.description}</p>
          </Card>

          {/* Creator Information */}
          <Card title="Creator Information">
            <div className="flex items-start">
              <User className="w-5 h-5 text-gray-400 mt-1 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-500">Created By</p>
                <p className="text-base text-gray-900">{assessment.createdBy?.fullName || 'N/A'}</p>
                <p className="text-sm text-gray-500">{assessment.createdBy?.email || ''}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {assessment.createdAt ? format(new Date(assessment.createdAt), 'dd MMM yyyy, HH:mm') : ''}
                </p>
              </div>
            </div>
          </Card>

          {/* Content */}
          <Card title="Assessment Questions" className="lg:col-span-2">
            {assessment.questions && assessment.questions.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 mb-4">
                  Total Questions: {assessment.questions.length}
                </p>
                {assessment.questions.map((question, index) => (
                  <div key={question._id || index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary-700">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 mb-2">{question.questionText}</p>
                        <div className="flex flex-wrap gap-2">
                          {question.options?.map((option, optIndex) => (
                            <span
                              key={optIndex}
                              className="px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-xs text-gray-700"
                            >
                              {option.value}. {option.label}
                            </span>
                          ))}
                        </div>
                        <div className="mt-3 flex items-center gap-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Correct Answer: {question.correctAnswer}
                          </span>
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Difficulty: {question.difficultyLevel}
                          </div>
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Quotient: {question.quotient}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No questions available</p>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'scoring' && (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Scoring Analytics</p>
                <h2 className="text-xl font-bold text-gray-900 mt-1">Assessment Performance</h2>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary-600" strokeWidth={1.5} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select
                value={scoringFilters.collegeId}
                onChange={(e) => handleScoringFilterChange('collegeId', e.target.value)}
                className="h-10 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-colors"
              >
                <option value="">All Institutes</option>
                {(scoringFilterOptions.colleges || []).map(college => (
                  <option key={college._id} value={college._id}>{college.name}</option>
                ))}
              </select>
              <select
                value={scoringFilters.department}
                onChange={(e) => handleScoringFilterChange('department', e.target.value)}
                className="h-10 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-colors"
              >
                <option value="">All Departments</option>
                {(scoringFilterOptions.departments || []).map((department, idx) => {
                  const deptVal = typeof department === 'object' && department !== null ? (department.abbreviation || department.fullName || department.uniqueId) : department;
                  const display = deptVal ? String(deptVal) : `Dept ${idx + 1}`;
                  return (
                    <option key={display} value={display}>{display}</option>
                  );
                })}
              </select>
              <select
                value={scoringFilters.batch}
                onChange={(e) => handleScoringFilterChange('batch', e.target.value)}
                className="h-10 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-colors"
              >
                <option value="">All Batches</option>
                {(scoringFilterOptions.batches || []).map((batch, idx) => {
                  const batchVal = typeof batch === 'object' && batch !== null ? (batch.batch || batch) : batch;
                  const display = batchVal ? String(batchVal) : `Batch ${idx + 1}`;
                  return (
                    <option key={display} value={display}>{display}</option>
                  );
                })}
              </select>
              <select
                value={scoringFilters.studentId}
                onChange={(e) => handleScoringFilterChange('studentId', e.target.value)}
                className="h-10 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-colors"
              >
                <option value="">All Students</option>
                {(scoringFilterOptions.students || []).map(student => (
                  <option key={student._id} value={student._id}>
                    {student.fullName} {student.studentId ? `(${student.studentId})` : ''}
                  </option>
                ))}
              </select>
              <input
                value={scoringFilters.search}
                onChange={(e) => handleScoringFilterChange('search', e.target.value)}
                placeholder="Search student"
                className="h-10 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          {scoringLoading ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : scoringAnalytics ? (
            <>
              {/* Scoring Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <Clock className="w-5 h-5 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Total Attempts</p>
                    <p className="text-xl font-bold text-gray-900 leading-none mt-1">{scoringAnalytics.totalAttempts || scoringAnalytics.totalResponses || 0}</p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <TrendingUp className="w-5 h-5 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Average Score</p>
                    <p className="text-xl font-bold text-gray-900 leading-none mt-1">
                      {Number(scoringAnalytics.averageScore || 0).toFixed(1)}%
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <Award className="w-5 h-5 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Highest Score</p>
                    <p className="text-xl font-bold text-gray-900 leading-none mt-1">
                      {scoringAnalytics.highestScore}%
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <Target className="w-5 h-5 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Lowest Score</p>
                    <p className="text-xl font-bold text-gray-900 leading-none mt-1">
                      {scoringAnalytics.lowestScore}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Analytics Visualization */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Score Distribution" className="border-gray-200 shadow-sm">
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={scoringAnalytics.distribution}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} dx={-10} />
                                <Tooltip cursor={{fill: '#F3F4F6'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}} />
                                <Bar dataKey="value" fill="#1a3884" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card title="Performance Share" className="border-gray-200 shadow-sm">
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={scoringAnalytics.distribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {scoringAnalytics.distribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
              </div>
              {/* Individual Scores List */}
              <Card title="Individual Submissions" className="border-gray-200 shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Student</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Attempt</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Score</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Submitted At</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {scoringResponses.map((resp, index) => (
                                <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-9 w-9 rounded-full bg-[#1a3884] flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                                                {(resp.student?.fullName || resp.user?.fullName)?.charAt(0) || 'S'}
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-sm font-semibold text-gray-900">{resp.student?.fullName || resp.user?.fullName || 'Academic User'}</div>
                                                <div className="text-xs text-gray-500">{resp.student?.email || resp.user?.email || 'N/A'}</div>
                                                {(resp.student?.department || resp.student?.batch) && (
                                                  <div className="text-xs text-gray-400 mt-0.5">
                                                    {[resp.student?.department, resp.student?.batch].filter(Boolean).join(' • ')}
                                                  </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center justify-center h-6 min-w-6 px-2 rounded bg-gray-100 text-gray-700 text-xs font-semibold border border-gray-200">
                                          #{resp.attemptNumber || 1}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">{resp.percentage}%</div>
                                        <div className="text-xs text-gray-500">{resp.score} / {resp.totalScore} pts</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <Badge variant={resp.percentage >= (assessment.passingScore || 50) ? 'success' : 'danger'}>
                                            {resp.percentage >= (assessment.passingScore || 50) ? 'Pass' : 'Fail'}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {resp.submittedAt ? format(new Date(resp.submittedAt), 'MMM dd, yyyy hh:mm a') : 'N/A'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
              </Card>
            </>
          ) : (
            <div className="py-20 text-center bg-white rounded-xl border border-dashed border-gray-300">
                <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No submission data available</h3>
                <p className="text-gray-500 max-w-xs mx-auto mt-2">Charts and statistics will appear once students start submitting their assessments.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssessmentView;
